import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, Button, Typography, Box, Paper, Alert, CircularProgress } from '@mui/material';
import { login, resendVerification } from '../api/auth';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const navigate = useNavigate();
  const { fetchMe } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification('');
    setResendMsg('');
    try {
      await login(email, password);
      await fetchMe();
      navigate('/');
    } catch (err) {
      const data = err.response?.data;
      if (data?.needsVerification) {
        setNeedsVerification(data.email || email);
      } else {
        setError(data?.message || 'Login failed');
      }
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    try {
      await resendVerification(needsVerification);
      setResendMsg('Verification email sent. Check your inbox (and spam folder).');
    } catch (err) {
      setResendMsg(err.response?.data?.message || 'Failed to resend verification email');
    }
    setResending(false);
  };

  return (
    <Box className="water-bg" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8faff' }}>
      <Paper className="glass-card" sx={{ p: 4, maxWidth: 400, width: '100%', borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src="/talktokam-green-wa.png" alt="Talktokam"
            sx={{ height: 48, width: 'auto', borderRadius: '8px', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#075e54' }}>WhatsApp API Platform</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Login to your account</Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {needsVerification && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Please verify your email before logging in. Check your inbox (and spam folder) for the verification link.
            </Typography>
            <Button size="small" variant="contained" onClick={handleResend} disabled={resending}
              sx={{ bgcolor: '#075e54', '&:hover': { bgcolor: '#0a7a6c' }, textTransform: 'none' }}>
              {resending ? <CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} /> : null}
              Resend verification email
            </Button>
            {resendMsg && (
              <Typography variant="body2" sx={{ mt: 1 }} color={resendMsg.startsWith('Failed') || resendMsg.startsWith('User') ? 'error' : '#166534'}>
                {resendMsg}
              </Typography>
            )}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <TextField label="Email" type="email" fullWidth required value={email} onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="Password" type="password" fullWidth required value={password} onChange={e => setPassword(e.target.value)} sx={{ mb: 3 }} />
          <Button type="submit" variant="contained" fullWidth sx={{ py: 1.2 }}>
            Login
          </Button>
        </form>
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: '#64748b' }}>
          Don't have an account? <Link to="/register" style={{ color: '#075e54', fontWeight: 600, textDecoration: 'none' }}>Register</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
